import { useEffect, useRef, useState } from 'react';
import { IVA_RATES } from '../../services/taxes.js';
import { computeItemSubtotal } from '../../services/invoices.js';

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});

function formatCurrency(value) {
  return currencyFormatter.format(Number.isFinite(value) ? value : 0);
}

export default function ItemsTable({
  items,
  onItemChange,
  onRemoveItem,
  onAddProductClick,
  onAddConceptClick,
  title = 'Ítems',
  emptyMessage = 'Agregá productos o servicios para comenzar.',
  addConceptLabel = 'Agregar concepto',
  addProductLabel = '+ Agregar ítem',
  showProductButton = true,
  secondaryActionLabel = '',
  onSecondaryActionClick,
  autoFocusNewItemField = null,
  manualEntry = false,
}) {
  const previousItemsLengthRef = useRef(items.length);
  const fieldRefs = useRef({});
  const [manualPriceDrafts, setManualPriceDrafts] = useState({});

  useEffect(() => {
    const shouldFocusNewItem = autoFocusNewItemField && items.length > previousItemsLengthRef.current;
    previousItemsLengthRef.current = items.length;

    if (!shouldFocusNewItem || items.length === 0) {
      return;
    }

    const lastItem = items[items.length - 1];
    const focusKey = `${lastItem?.uid || lastItem?.id || items.length - 1}:${autoFocusNewItemField}`;
    const field = fieldRefs.current[focusKey];

    if (!field) {
      return;
    }

    window.requestAnimationFrame(() => {
      field.focus();
      if (typeof field.select === 'function') {
        field.select();
      }
    });
  }, [autoFocusNewItemField, items]);

  const handleChange = (index, field, value) => {
    if (!onItemChange) {
      return;
    }
    onItemChange(index, {
      ...items[index],
      [field]: value,
    });
  };

  const handleManualPriceChange = (index, rowKey, value) => {
    setManualPriceDrafts(drafts => ({ ...drafts, [rowKey]: value }));
    const normalized = value.replace(',', '.');
    if (!normalized) {
      handleChange(index, 'price', 0);
      return;
    }
    if (normalized === '-' || normalized === '+' || /[.,]$/.test(normalized)) return;
    const price = Number(normalized);
    if (Number.isFinite(price)) handleChange(index, 'price', price);
  };

  const handleManualPriceBlur = (index, rowKey) => {
    const value = manualPriceDrafts[rowKey];
    if (value === undefined) return;
    const normalized = value.replace(',', '.');
    const price = Number(normalized);
    if (!['-', '+', '.', '-.', '+.'].includes(normalized) && Number.isFinite(price)) {
      handleChange(index, 'price', price);
    }
    setManualPriceDrafts(drafts => {
      const next = { ...drafts };
      delete next[rowKey];
      return next;
    });
  };

  return (
    <div className="invoice-card">
      <div className="invoice-card__header">
        <h3 className="invoice-card__title">{title}</h3>
        <div className="invoice-items__actions">
          {secondaryActionLabel && onSecondaryActionClick ? (
            <button type="button" className="btn btn--secondary" onClick={onSecondaryActionClick}>
              {secondaryActionLabel}
            </button>
          ) : null}
          <button type="button" className={`btn ${secondaryActionLabel ? 'btn--primary' : 'btn--secondary'}`} onClick={onAddConceptClick}>
            {addConceptLabel}
          </button>
          {showProductButton ? (
            <button type="button" className="btn btn--primary" onClick={onAddProductClick}>
              {addProductLabel}
            </button>
          ) : null}
        </div>
      </div>
      <div className="invoice-card__body">
        {items.length === 0 ? (
          <div className="invoice-items__empty">{emptyMessage}</div>
        ) : (
          <div className="invoice-items-table-wrap">
            <table className={`invoice-items-table${manualEntry ? ' invoice-items-table--manual' : ''}`}>
              <colgroup>
                {!manualEntry ? <col className="invoice-items-table__col invoice-items-table__col--code" /> : null}
                <col className="invoice-items-table__col invoice-items-table__col--description" />
                <col className="invoice-items-table__col invoice-items-table__col--qty" />
                <col className="invoice-items-table__col invoice-items-table__col--price" />
                <col className="invoice-items-table__col invoice-items-table__col--iva" />
                <col className="invoice-items-table__col invoice-items-table__col--disc" />
                <col className="invoice-items-table__col invoice-items-table__col--subtotal" />
                <col className="invoice-items-table__col invoice-items-table__col--actions" />
              </colgroup>
              <thead>
                <tr>
                  {!manualEntry ? <th>Código</th> : null}
                  <th>Descripción</th>
                  <th>Cant.</th>
                  <th>Precio unit.</th>
                  <th>IVA</th>
                  <th>Desc %</th>
                  <th>Subtotal</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const subtotal = computeItemSubtotal(item);
                  const rowKey = item.uid || item.id || index;

                  return (
                    <tr key={rowKey}>
                      {!manualEntry ? <td data-label="Código">
                        <input
                          ref={element => {
                            fieldRefs.current[`${rowKey}:codigo`] = element;
                          }}
                          value={item.codigo || ''}
                          placeholder="Código"
                          onChange={event => handleChange(index, 'codigo', event.target.value)}
                        />
                      </td> : null}
                      <td data-label="Descripción">
                        {manualEntry ? (
                        <textarea
                          ref={element => {
                            fieldRefs.current[`${rowKey}:descripcion`] = element;
                            if (element) {
                              element.style.height = 'auto';
                              element.style.height = `${element.scrollHeight}px`;
                            }
                          }}
                          value={item.descripcion || ''}
                          placeholder="Descripción"
                          rows={1}
                          onInput={event => {
                            event.currentTarget.style.height = 'auto';
                            event.currentTarget.style.height = `${event.currentTarget.scrollHeight}px`;
                          }}
                          onChange={event => handleChange(index, 'descripcion', event.target.value)}
                        />
                        ) : (
                          <input
                            ref={element => {
                              fieldRefs.current[`${rowKey}:descripcion`] = element;
                            }}
                            value={item.descripcion || ''}
                            placeholder="Descripción"
                            onChange={event => handleChange(index, 'descripcion', event.target.value)}
                          />
                        )}
                      </td>
                      <td className="invoice-items-table__qty" data-label="Cant.">
                        <input
                          type="number"
                          step="0.01"
                          value={item.qty ?? ''}
                          onChange={event => handleChange(index, 'qty', event.target.value)}
                        />
                      </td>
                      <td className="invoice-items-table__price" data-label="Precio unit.">
                        <input
                          type={manualEntry ? 'text' : 'number'}
                          inputMode={manualEntry ? 'decimal' : undefined}
                          step={manualEntry ? undefined : '0.01'}
                          aria-label="Precio unitario"
                          value={manualEntry ? (manualPriceDrafts[rowKey] ?? item.price ?? '') : (item.price ?? '')}
                          onChange={event => (manualEntry
                            ? handleManualPriceChange(index, rowKey, event.target.value)
                            : handleChange(index, 'price', event.target.value))}
                          onBlur={manualEntry ? () => handleManualPriceBlur(index, rowKey) : undefined}
                        />
                      </td>
                      <td data-label="IVA">
                        <select
                          value={item.iva ?? ''}
                          onChange={event => handleChange(index, 'iva', Number(event.target.value))}
                        >
                          {IVA_RATES.map(rate => (
                            <option key={rate} value={rate}>
                              {rate}%
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="invoice-items-table__disc" data-label="Desc %">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={item.discPct ?? 0}
                          onChange={event => handleChange(index, 'discPct', event.target.value)}
                        />
                      </td>
                      <td className="invoice-items-table__subtotal" data-label="Subtotal">
                        {formatCurrency(subtotal)}
                      </td>
                      <td className="invoice-items-table__actions" data-label="Acciones">
                        <button
                          type="button"
                          className="invoice-items-table__remove"
                          onClick={() => onRemoveItem(index)}
                          aria-label="Eliminar ítem"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
